import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, UploadCloud, X } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { DOC_TYPES } from "@/lib/labels";

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocSlot = ({ docType, label, doc, progress, onFile, onRemove }) => {
  const inputRef = useRef(null);
  return (
    <div className="xy-card p-5" data-testid={`doc-slot-${docType}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display font-semibold text-slate-100 text-sm">{label}</p>
          <p className="text-xs text-slate-500 mt-1">PDF, JPG, PNG, DOC — max 25MB</p>
        </div>
        {doc ? (
          <div className="flex items-center gap-3 min-w-0">
            <FileText size={18} className="text-violet-300 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-slate-200 truncate max-w-[160px]" data-testid={`doc-filename-${docType}`}>
                {doc.original_filename}
              </p>
              <p className="text-[11px] text-slate-500">{formatSize(doc.size)}</p>
            </div>
            <button
              type="button"
              data-testid={`btn-remove-doc-${docType}`}
              onClick={() => onRemove(docType)}
              className="text-slate-500 hover:text-red-400 transition-colors"
              aria-label={`Remove ${label}`}
            >
              <X size={16} />
            </button>
          </div>
        ) : progress != null ? (
          <div className="w-32">
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full bg-violet-500 transition-all duration-200"
                style={{ width: `${progress}%` }}
                data-testid={`doc-progress-${docType}`}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1 text-right">{progress}%</p>
          </div>
        ) : (
          <button
            type="button"
            data-testid={`btn-upload-doc-${docType}`}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-full border border-violet-500/40 bg-violet-500/10 px-4 py-2 text-xs font-semibold text-violet-300 hover:bg-violet-500/20 transition-colors"
          >
            <UploadCloud size={14} /> Upload
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        data-testid={`input-file-${docType}`}
        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx"
        onChange={(e) => {
          onFile(docType, e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </div>
  );
};

export const StepDocuments = ({ form, update }) => {
  const [uploading, setUploading] = useState({});

  const handleFile = async (docType, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    setUploading((u) => ({ ...u, [docType]: 0 }));
    try {
      const res = await axios.post(`${API}/onboarding/upload`, fd, {
        onUploadProgress: (e) => setUploading((u) => ({ ...u, [docType]: Math.round((e.progress || 0) * 100) })),
      });
      const others = form.documents.filter((d) => d.doc_type !== docType);
      update("documents", [...others, { doc_type: docType, ...res.data }]);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading((u) => {
        const next = { ...u };
        delete next[docType];
        return next;
      });
    }
  };

  const handleRemove = (docType) => {
    update(
      "documents",
      form.documents.filter((d) => d.doc_type !== docType)
    );
  };

  return (
    <div>
      <p className="text-sm text-slate-400 mb-6">
        Upload what you have now — you can always send the rest to the Xyion team later.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DOC_TYPES.map(({ key, label }) => (
          <DocSlot
            key={key}
            docType={key}
            label={label}
            doc={form.documents.find((d) => d.doc_type === key)}
            progress={uploading[key]}
            onFile={handleFile}
            onRemove={handleRemove}
          />
        ))}
      </div>
    </div>
  );
};
